import { cellValidationText, CompanyReportAdminTranslation } from 'app/area/company-reports-administration/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ComplexValue, ValidationLevel, ValidationMessage, ValidationProps } from 'ui';
import { languageValidationParameter } from '../constants';
import { CompanyReportItem, CompanyReportItemValidation, TextValue, MetadataValue } from '../types-store';
import { CompanyReportFormIds } from 'app/area/company-reports-administration/form/constants';
import { emailBodyFieldsRequired } from 'app/area/company-reports-administration/form/components/helpers';
import { MetadataValidationError, TextsValidationError } from 'app/area/company-reports-administration/form/types';

const validationMessageHelper = (propName: string, messages: ValidationMessage | ValidationMessage[]) => {
  return {
    [propName]: {
      level: ValidationLevel.error,
      messages
    }
  };
};

export const validateCompanyReport = (
  companyReport: CompanyReportItem,
  languageOptions: ComplexValue<number>[],
  translation: CompanyReportAdminTranslation
): CompanyReportItemValidation => {

  const defaultValidationError = {
    level: ValidationLevel.error,
    message: translation.must_be_filled_out
  };

  const descriptionValidationError = {
    level: ValidationLevel.error,
    message: translation.no_text
  };

  const allLanguages = (languageOptions || [])
    .map(option => (companyReport && companyReport.language || [])
      .find(language => language.value === option.value)
      || { value: option.value }
    );

  const languageValidation = allLanguages
    .map(language =>
      language.description === undefined || language.description.trim() === ''
        ? {
          ...defaultValidationError,
          parameters: {
            [languageValidationParameter]: language.value
          }
        } as ValidationMessage
        : undefined
    )
    .filter(validation => !!validation);

  const textIdValidation = !isDefinedIncludingZero(companyReport?.textId)
    ? defaultValidationError
    : undefined;

  const textSetValidation = !isDefinedIncludingZero(companyReport?.textSet?.value)
    ? defaultValidationError
    : undefined;

  const textDescriptionValidation = !isDefinedIncludingZero(companyReport?.textDescription)
    ? descriptionValidationError
    : undefined;

  const textsValidation = validateTexts(companyReport?.texts, translation);
  const metadataValidation = validateMetadata(companyReport?.metadata, translation);

  return {
    ...(languageValidation.length > 0 && validationMessageHelper(CompanyReportFormIds.language, languageValidation)),
    ...(!!emailBodyFieldsRequired(companyReport) && {
      ...(!!textIdValidation && validationMessageHelper(CompanyReportFormIds.textId, [textIdValidation])),
      ...(!!textSetValidation && validationMessageHelper(CompanyReportFormIds.textSet, [textSetValidation])),
      ...(!!textDescriptionValidation && validationMessageHelper(CompanyReportFormIds.textDescription, [textDescriptionValidation]))
    }),
    ...(textsValidation && { text: textsValidation }),
    ...(metadataValidation && { metadata: metadataValidation })
  };
};

export const validateTexts = (
  texts: TextValue[],
  translation: CompanyReportAdminTranslation
): ValidationProps[] => {
  const validationError = (message: string = translation.must_be_filled_out) => {
    return {
      level: ValidationLevel.error,
      messages: [
        {
          level: ValidationLevel.error,
          message: message
        }]
    };
  };

  const duplicatedTextsValidationErrors = (textValue: TextValue) => {
    const duplicatedTexts = texts?.filter(item =>
      item.textId === textValue.textId &&
      item.textSet?.value === textValue.textSet?.value &&
      item.cell?.toLowerCase()?.trimEnd() === textValue.cell?.toLowerCase()?.trimEnd());

    const itemErrors = duplicatedTexts?.flatMap<TextsValidationError>(text => [
      {
        id: text.id,
        field: CompanyReportFormIds.textId,
        error: validationError('')
      }, {
        id: text.id,
        field: CompanyReportFormIds.textSet,
        error: validationError('')
      },
      {
        id: text.id,
        field: CompanyReportFormIds.textDescription,
        error: validationError('')
      },
      {
        id: text.id,
        field: CompanyReportFormIds.cell,
        error: validationError(cellValidationText)
      }
    ]).filter(text => text.id === textValue.id);

    return duplicatedTexts.length >= 2 ? itemErrors : [];
  };

  const validationErrors = texts?.flatMap<TextsValidationError>(item => {
    const itemErrors = [
      !item.textSet?.value && {
        id: item.id,
        field: CompanyReportFormIds.textSet,
        error: validationError()
      },
      !item.textId && {
        id: item.id,
        field: CompanyReportFormIds.textId,
        error: validationError()
      },
      !item.textDescription && {
        id: item.id,
        field: CompanyReportFormIds.textDescription,
        error: validationError(translation.no_text)
      },
      !item.cell?.trim() && {
        id: item.id,
        field: CompanyReportFormIds.cell,
        error: validationError()
      },
      ...duplicatedTextsValidationErrors(item)
    ].filter(error => error);

    return itemErrors.length > 0 ? itemErrors : undefined;
  }).filter(item => item);

  return validationErrors?.length > 0 ? validationErrors.map(error => ({
    ...error.error,
    messages: error.error.messages.map(message => ({
      ...message,
      parameters: {
        id: error.id,
        field: error.field
      }
    }))
  })) : undefined;
};

const validateMetadata = (
  metadata: MetadataValue[],
  translation: CompanyReportAdminTranslation
): ValidationProps[] => {
  const validationError = {
    level: ValidationLevel.error,
    messages: [
      {
        level: ValidationLevel.error,
        message: translation.must_be_filled_out
      }]
  };

  const validationErrors = metadata?.flatMap<MetadataValidationError>(item => {
    const itemErrors = [
      !item.metadataType?.value && {
        id: item.id,
        field: CompanyReportFormIds.metadataType,
        error: validationError
      },
      !item?.cell && {
        id: item.id,
        field: CompanyReportFormIds.cell,
        error: validationError
      }].filter(error => error);

    return itemErrors.length > 0 ? itemErrors : undefined;
  }).filter(item => item);

  return validationErrors?.length > 0 ? validationErrors.map(error => ({
    ...error.error,
    messages: error.error.messages.map(message => ({
      ...message,
      parameters: {
        id: error.id,
        field: error.field
      }
    }))
  })) : undefined;
};
